import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, IconButton } from '@mui/material';
import plusIcon from '../../assets/icons/plus.svg';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import chevronLeftIcon from '../../assets/icons/chevron-left.svg';
import chevronRightIcon from '../../assets/icons/chevron-right.svg';
import checkIcon from '../../assets/icons/check.svg';
import CaisseFilters from './CaisseFilters';
import CreationModal from '../../components/ui/CreationModal';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import CustomSnackbar from '../../components/ui/Snackbar';
import editIcon from '../../assets/icons/edit.svg';
import trashIcon from '../../assets/icons/trash.svg';
import CircularIconButton from '../../components/ui/CircularIconButton';
import StatusPill from '../../components/ui/StatusPill';
import { caisseAPI } from '../../services/api';

// Force reload - TablePagination removed
const Caisses = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [caisses, setCaisses] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, caisse: null });
  const [editConfirmDialog, setEditConfirmDialog] = useState({ open: false, formData: null });
  
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    total: 0
  });

  const [createModal, setCreateModal] = useState({ open: false });

  // Success/Error feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  const [filters, setFilters] = useState({
    searchTerm: '',
    active: ''
  });

  const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);
  const [isStuck, setIsStuck] = useState(false);

  const STICKY_TOP_OFFSET = 76;

  const stickyOuterSx = { ...contentContainerSx };

  // Fetch caisses
  const fetchCaisses = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        size: pagination.size,
        ...(filters.searchTerm && { searchTerm: filters.searchTerm }),
        ...(filters.active && { active: filters.active === 'true' })
      };
      
      const { data } = await caisseAPI.getAll(params);
      
      if (data && typeof data === 'object' && 'content' in data) {
        // Backend handles all filtering and pagination
        setCaisses(data.content || []);
        setPagination(prev => ({ 
          ...prev, 
          total: data.totalElements || 0 
        }));
      } else {
        // Fallback for non-paginated response
        let items = Array.isArray(data) ? data : [];
        
        if (filters.searchTerm?.trim()) {
          const term = filters.searchTerm.trim().toLowerCase();
          items = items.filter(c => (c.name || '').toLowerCase().includes(term));
        }
        if (filters.active) {
          const isActive = filters.active === 'true';
          items = items.filter(c => c.active === isActive);
        }
        setCaisses(items);
        setPagination(prev => ({ ...prev, total: items.length }));
      }
    } catch (err) {
      console.error('Error fetching caisses:', err);
      setError('Erreur lors du chargement des caisses');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.size]);

  // Load caisses on component mount and when filters change
  useEffect(() => {
    fetchCaisses();
  }, [fetchCaisses]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      active: ''
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // Handle pagination changes
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPagination(prev => ({ 
      ...prev, 
      size: newSize,
      page: 0 
    }));
  };

  // Handle create caisse modal
  const handleCreateCaisse = () => {
    setCreateModal({ open: true });
  };

  const handleSubmitCreateCaisse = async (formData) => {
    // Check if it's an edit operation
    const isEdit = createModal.edit;
    
    // If editing, check for changes
    if (isEdit) {
      const nameChanged = formData.name !== createModal.edit.name;
      const activeChanged = formData.active !== !!createModal.edit.active;
      const hasChanges = nameChanged || activeChanged;
      
      if (!hasChanges) {
        setCreateModal({ open: false });
        return;
      }
      
      // Determine which confirmation dialog to show
      if (nameChanged && activeChanged) {
        // Both name and toggle changed - show generic confirmation
        setEditConfirmDialog({ open: true, formData, type: 'generic' });
      } else if (nameChanged) {
        // Only name changed - show generic confirmation
        setEditConfirmDialog({ open: true, formData, type: 'generic' });
      } else if (activeChanged) {
        // Only toggle changed - show specific activate/deactivate dialog
        const isActivating = formData.active && !createModal.edit.active;
        setEditConfirmDialog({ 
          open: true, 
          formData, 
          type: isActivating ? 'activate' : 'deactivate',
          caisseName: createModal.edit.name
        });
      }
      return;
    }
    
    // For create operations, proceed directly
    await performSubmit(formData, false);
  };

  const performSubmit = async (formData, isEdit) => {
    setLoading(true);
    try {
      if (isEdit) {
        // Update existing caisse
        await caisseAPI.update(createModal.edit.id, { 
          name: formData.name, 
          active: formData.active 
        });
        
        setSnackbar({ 
          open: true, 
          message: 'Caisse modifiée avec succès', 
          severity: 'success' 
        });
      } else {
        // Create new caisse
        await caisseAPI.create({ 
          name: formData.name, 
          code: formData.code,
          active: formData.active 
        });
        
        setSnackbar({ 
          open: true, 
          message: 'Caisse créée avec succès', 
          severity: 'success' 
        });
      }
      
      setCreateModal({ open: false });
      setEditConfirmDialog({ open: false, formData: null });
      fetchCaisses(); // Refresh the list
    } catch (err) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} caisse:`, err);
      setSnackbar({ 
        open: true, 
        message: `Erreur lors de la ${isEdit ? 'modification' : 'création'} de la caisse`, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmEdit = async () => {
    if (editConfirmDialog.formData) {
      await performSubmit(editConfirmDialog.formData, true);
    }
  };

  const handleDelete = (caisse) => setDeleteDialog({ open: true, caisse });
  const confirmDelete = async () => {
    if (!deleteDialog.caisse) return;
    setLoading(true);
    try {
      await caisseAPI.delete(deleteDialog.caisse.id);
      setSnackbar({ open: true, message: 'Caisse supprimée avec succès', severity: 'success' });
      setDeleteDialog({ open: false, caisse: null });
      fetchCaisses();
    } catch (err) {
      console.error('Error deleting caisse:', err);
      
      // Check if it's a conflict error (has transactions)
      if (err.response?.status === 409) {
        // Show confirmation dialog for disabling instead
        setDeleteDialog({ 
          open: true, 
          caisse: deleteDialog.caisse,
          mode: 'disable' // New mode for disable confirmation
        });
      } else {
        setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle disable instead of delete
  const confirmDisable = async () => {
    if (!deleteDialog.caisse) return;
    setLoading(true);
    try {
      await caisseAPI.deactivate(deleteDialog.caisse.id);
      setSnackbar({ open: true, message: 'Caisse désactivée avec succès', severity: 'success' });
      setDeleteDialog({ open: false, caisse: null });
      fetchCaisses();
    } catch (err) {
      console.error('Error disabling caisse:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la désactivation', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Field configuration for creation modal
  const caisseFields = [
    {
      name: 'name',
      type: 'text',
      placeholder: 'Nom de la caisse',
      required: true
    },
    // Code field only for creation, not for editing
    ...(createModal.edit ? [] : [{
      name: 'code',
      type: 'text',
      placeholder: 'Code (ex: COPBO)',
      required: true,
      inputProps: {
        maxLength: 5
      },
      transform: (value) => value.toUpperCase(),
      validation: (value) => {
        const code = value.trim().toUpperCase();
        if (code.length < 2) {
          return 'Le code doit contenir au moins 2 caractères';
        }
        if (code.length > 5) {
          return 'Le code ne peut pas dépasser 5 caractères';
        }
        if (!/^[A-Z]+$/.test(code)) {
          return 'Le code ne peut contenir que des lettres majuscules';
        }
        return null;
      }
    }])
  ];

  // Memoize initialData to prevent form reset during confirmation dialog
  const initialData = useMemo(() => {
    return createModal.edit ? 
      { name: createModal.edit.name, active: !!createModal.edit.active } : 
      { name: '', active: true };
  }, [createModal.edit]);

  // Simple scroll listener for background color change
  useEffect(() => {
    const handleScroll = () => {
      setIsStuck(window.scrollY > STICKY_TOP_OFFSET);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Sticky Header Section */}
      <Box
        className="caisses-sticky-header"
        sx={{
          position: 'sticky',
          top: '0px',
          zIndex: 1000,
          backgroundColor: isStuck ? '#F8F8F8' : 'transparent',
          borderBottom: isStuck ? '1px solid #E5E8EF' : 'none',
          transition: 'background-color 0.2s ease, border-bottom 0.2s ease'
        }}
      >
        <Box sx={stickyOuterSx}>
          <Box sx={{ paddingTop: '24px', paddingBottom: '8px' }}>
            {/* Page Title & Create Button */}
            <Box sx={{ marginBottom: '8px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '28px',
                  lineHeight: '32px',
                  letterSpacing: '0px',
                  color: '#38485C',
                  opacity: 0.88,
                  textTransform: 'none',
                  margin: 0
                }}
              >
                Caisses
              </h1>

              <Button
                variant="contained"
                startIcon={<img src={plusIcon} alt="plus" style={{ width: '16px', height: '16px' }} />}
                onClick={handleCreateCaisse}
                sx={{
                  height: '40px',
                  backgroundColor: '#027BC6',
                  boxShadow: '0px 20px 80px #00000011',
                  borderRadius: '12px',
                  opacity: 1,
                  whiteSpace: 'nowrap',
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '17px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#025a9c',
                  },
                }}
              >
                Créer une nouvelle caisse
              </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ width: '100%', overflow: 'visible', position: 'relative', zIndex: 1001 }}>
              <CaisseFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onReset={handleResetFilters}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ ...contentContainerSx, paddingBottom: '32px' }}>
        <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
              gap: '16px',
              '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
              '@media (min-width: 1200px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            {caisses.map((caisse) => (
              <Box
                key={caisse.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  boxShadow: '0px 6px 26px #626B7D12',
                  padding: '16px 20px',
                }}
              >
                <Box>
                  <Typography sx={{ 
                    fontFamily: 'Montserrat', 
                    fontWeight: 600, 
                    fontSize: '18px', 
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#38485C',
                    opacity: 1,
                    textAlign: 'left'
                  }}>
                    {caisse.name}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <StatusPill
                      text={caisse.active ? 'Actif' : 'Inactif'}
                      color={caisse.active ? '#0AD69F' : '#EF456E'}
                      customPadding="2px 10px"
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularIconButton
                    icon={editIcon}
                    alt="Edit"
                    aria-label="edit"
                    onClick={() => setCreateModal({ open: true, edit: caisse })}
                  />
                  <CircularIconButton
                    icon={trashIcon}
                    alt="Delete"
                    aria-label="delete"
                    onClick={() => handleDelete(caisse)}
                  />
                </Box>
              </Box>
            ))}
            {!caisses.length && (
              <Typography sx={{ gridColumn: '1 / -1', color: '#6B7280', textAlign: 'center', py: 4 }}>
                {filters.searchTerm || filters.active ? 
                  'Aucune caisse trouvée avec ces critères' : 
                  'Aucune caisse trouvée'
                }
              </Typography>
            )}
          </Box>
        )}
        
        {/* Custom Pagination Controls */}
        {!loading && !error && caisses.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            py: 2,
            px: 2
          }}>
            {/* Left side - Show rows dropdown + count */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                Afficher les lignes
              </Typography>
              <Box sx={{ position: 'relative' }} data-dropdown>
                <Box 
                  onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                  sx={{
                    width: '60px',
                    height: '32px',
                    background: '#FFFFFF',
                    boxShadow: '0px 20px 80px #00000011',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    gap: 0.5
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600 }}>
                    {pagination.size}
                  </Typography>
                  <img 
                    src={chevronDownIcon} 
                    alt="dropdown" 
                    style={{ width: '8px', height: '8px' }}
                  />
                </Box>
                
                {/* Dropdown menu - appears above */}
                {isRowsDropdownOpen && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    background: '#FFFFFF',
                    boxShadow: '0px 20px 80px #00000011',
                    borderRadius: '12px',
                    zIndex: 1000,
                    minWidth: '60px',
                    marginBottom: '4px'
                  }}>
                    {[10, 20, 50, 100].map((size) => (
                      <Box
                        key={size}
                        onClick={() => {
                          handleRowsPerPageChange({ target: { value: size } });
                          setIsRowsDropdownOpen(false);
                        }}
                        sx={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          '&:hover': {
                            background: '#f5f5f5'
                          },
                          '&:first-of-type': {
                            borderTopLeftRadius: '12px',
                            borderTopRightRadius: '12px'
                          },
                          '&:last-of-type': {
                            borderBottomLeftRadius: '12px',
                            borderBottomRightRadius: '12px'
                          }
                        }}
                      >
                        {size}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              
              {/* Row count next to dropdown */}
              <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                {pagination.page * pagination.size + 1}-{Math.min((pagination.page + 1) * pagination.size, pagination.total)} sur {pagination.total}
              </Typography>
            </Box>

            {/* Middle - Page navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Previous button */}
              <IconButton
                onClick={() => handlePageChange(null, pagination.page - 1)}
                disabled={pagination.page === 0}
                sx={{
                  width: '40px',
                  height: '40px',
                  background: '#FFFFFF',
                  boxShadow: '0px 20px 80px #00000011',
                  borderRadius: '12px',
                  '&:hover': {
                    background: '#f5f5f5'
                  },
                  '&:disabled': {
                    opacity: 0.5
                  }
                }}
              >
                <img 
                  src={chevronLeftIcon} 
                  alt="previous" 
                  style={{ width: '16px', height: '16px' }}
                />
              </IconButton>

              {/* Page numbers */}
              {(() => {
                const totalPages = Math.ceil(pagination.total / pagination.size);
                const currentPage = pagination.page + 1;
                const pages = [];
                
                // Show first few pages
                for (let i = 1; i <= Math.min(4, totalPages); i++) {
                  pages.push(i);
                }
                
                // Show current page if not already shown
                if (currentPage > 4 && currentPage < totalPages - 3) {
                  pages.push('...');
                  pages.push(currentPage);
                  pages.push('...');
                }
                
                // Show last few pages
                for (let i = Math.max(totalPages - 3, 5); i <= totalPages; i++) {
                  if (i > 4) pages.push(i);
                }
                
                return pages.map((page, index) => (
                  <Box
                    key={index}
                    onClick={() => {
                      if (typeof page === 'number') {
                        handlePageChange(null, page - 1);
                      }
                    }}
                    sx={{
                      width: '40px',
                      height: '40px',
                      background: page === currentPage ? '#027BC6' : '#FFFFFF',
                      color: page === currentPage ? '#FFFFFF' : '#666',
                      boxShadow: '0px 20px 80px #00000011',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: typeof page === 'number' ? 'pointer' : 'default',
                      fontSize: '14px',
                      fontWeight: 600,
                      '&:hover': typeof page === 'number' ? {
                        background: page === currentPage ? '#025a9c' : '#f5f5f5'
                      } : {}
                    }}
                  >
                    {page}
                  </Box>
                ));
              })()}

              {/* Next button */}
              <IconButton
                onClick={() => handlePageChange(null, pagination.page + 1)}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.size) - 1}
                sx={{
                  width: '40px',
                  height: '40px',
                  background: '#FFFFFF',
                  boxShadow: '0px 20px 80px #00000011',
                  borderRadius: '12px',
                  '&:hover': {
                    background: '#f5f5f5'
                  },
                  '&:disabled': {
                    opacity: 0.5
                  }
                }}
              >
                <img 
                  src={chevronRightIcon} 
                  alt="next" 
                  style={{ width: '16px', height: '16px' }}
                />
              </IconButton>
            </Box>
          </Box>
        )}
        </Box>
      </Box>

      {/* Creation Modal */}
      <CreationModal
        open={createModal.open}
        onClose={() => setCreateModal({ open: false })}
        onSubmit={handleSubmitCreateCaisse}
        title={createModal.edit ? "Modifier la caisse" : "Créer une nouvelle caisse"}
        fields={caisseFields}
        submitButtonText={createModal.edit ? "Enregistrer" : "Créer une nouvelle caisse"}
        loading={loading}
        toggleConfig={{
          field: 'active',
          activeLabel: 'Actif',
          inactiveLabel: 'Inactif'
        }}
        initialData={initialData}
      />

      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, caisse: null })}
        onConfirm={deleteDialog.mode === 'disable' ? confirmDisable : confirmDelete}
        type={deleteDialog.mode === 'disable' ? 'deactivate' : 'delete'}
        title={deleteDialog.mode === 'disable' ? 'Désactiver la caisse' : 'Supprimer la caisse'}
        messageComponent={deleteDialog.mode === 'disable' ? (
          <Typography
            sx={{
              textAlign: 'center',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '32px',
              letterSpacing: '0px',
              opacity: 1,
              color: '#333333'
            }}
          >
            Cette caisse a au moins une transaction. Elle sera{' '}
            <span style={{ color: '#027BC6' }}>désactivée</span> au lieu d'être supprimée.
            <br /><br />
            Êtes-vous sûr de vouloir désactiver la caisse{' '}
            <span style={{ color: '#007BC6' }}>{deleteDialog.caisse?.name || ''}</span>
            {' '}?
          </Typography>
        ) : undefined}
        message={deleteDialog.mode === 'disable' ? undefined : (
          <>
            Êtes-vous sûr de vouloir supprimer la caisse{' '}
            <span style={{ color: '#007BC6' }}>{deleteDialog.caisse?.name || ''}</span>
            {' '}?
          </>
        )}
        confirmText={deleteDialog.mode === 'disable' ? 'Désactiver' : 'Supprimer'}
        cancelText="Annuler"
        loading={loading}
        hasExtendedContent={deleteDialog.mode === 'disable'}
      />

      {/* Edit Confirmation Dialog */}
      {editConfirmDialog.type === 'generic' && (
        <ConfirmationDialog
          open={editConfirmDialog.open}
          onClose={() => setEditConfirmDialog({ open: false, formData: null })}
          onConfirm={confirmEdit}
          type="confirm"
          title="Confirmer les modifications"
          message="Êtes-vous sûr de vouloir enregistrer les modifications de cette caisse ?"
          confirmText="Enregistrer"
          cancelText="Annuler"
          loading={loading}
        />
      )}

      {/* Deactivate Confirmation Dialog */}
      {editConfirmDialog.type === 'deactivate' && (
        <ConfirmationDialog
          open={editConfirmDialog.open}
          onClose={() => setEditConfirmDialog({ open: false, formData: null })}
          onConfirm={confirmEdit}
          type="deactivate"
          title="Désactiver la caisse"
          messageComponent={
            <Typography
              sx={{
                textAlign: 'center',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '32px',
                letterSpacing: '0px',
                opacity: 1,
                color: '#333333'
              }}
            >
              Êtes-vous sûr de vouloir désactiver la caisse{' '}
              <span style={{ color: '#027BC6' }}>{editConfirmDialog.caisseName || ''}</span>
              {' '}?
            </Typography>
          }
          confirmText="Désactiver"
          cancelText="Annuler"
          loading={loading}
        />
      )}

      {/* Activate Confirmation Dialog */}
      {editConfirmDialog.type === 'activate' && (
        <ConfirmationDialog
          open={editConfirmDialog.open}
          onClose={() => setEditConfirmDialog({ open: false, formData: null })}
          onConfirm={confirmEdit}
          type="activate"
          title="Activer la caisse"
          messageComponent={
            <Typography
              sx={{
                textAlign: 'center',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '32px',
                letterSpacing: '0px',
                opacity: 1,
                color: '#333333'
              }}
            >
              Êtes-vous sûr de vouloir activer la caisse{' '}
              <span style={{ color: '#027BC6' }}>{editConfirmDialog.caisseName || ''}</span>
              {' '}?
            </Typography>
          }
          confirmText="Activer"
          cancelText="Annuler"
          loading={loading}
        />
      )}

      {/* Snackbar for feedback */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default Caisses;

